import { multiPolygon } from '@turf/turf'
import { LightningReportCustomPointSchema, LightningReportGeometrySchema } from '@xweather/protect-types'
import type { NonEmptyArray } from 'es-toolkit/types'
import i18next from 'i18next'
import { z } from 'zod'
import type { AssetLocation } from '../../../data/locations.tsx'
import { usePreferences } from '../../../data/userProfile.ts'
import { dayjs } from '../../../dayjs.ts'
import { createUseFormValues } from '../createUseFormValues.ts'

export const isNonEmpty = <T>(list: T[]): list is NonEmptyArray<T> => list.length > 0

// The fields every report type has; the shared steps and fields rely on these only.
export const BaseReportFormSchema = z.object({
	name: z
		.string()
		.trim()
		.nonempty({ error: () => i18next.t(($) => $.zod.nameRequired) }),
	assetVrns: z.array(z.string()).min(1, { error: () => i18next.t(($) => $.zod.pointOfInterestRequired) }),
	radiusKm: z.number(),
	// The asset (or custom point) buffered by `radiusKm`; the protect-types geometry schemas are not lightning specific
	geometry: LightningReportGeometrySchema,
	geometryTooComplex: z.boolean().refine((value) => !value), // error if true
	geometryPending: z.boolean().refine((value) => !value), // error if true
	customPoint: LightningReportCustomPointSchema.extend({
		isOutsideAssetGeometry: z.boolean().optional(),
	})
		.refine((value): boolean => !value.isOutsideAssetGeometry, {
			error: () => i18next.t(($) => $.reports.form.customPointOutsideAsset),
		})
		.optional(),
	preferences: z.object({ timezone: z.string() }),
})

export type BaseReportForm = z.infer<typeof BaseReportFormSchema>
export const useBaseFormValues = createUseFormValues<BaseReportForm>()

export const ReportTimeRangeSchema = z
	.object({
		start: z.iso.datetime({ error: () => i18next.t(($) => $.zod.startTimeRequired) }),
		end: z.iso.datetime({ error: () => i18next.t(($) => $.zod.endTimeRequired) }),
	})
	.refine(({ start, end }) => !start || !end || dayjs(end).isAfter(start), {
		error: () => i18next.t(($) => $.reports.form.timeRangeEndBeforeStart),
	})

export type ReportTimeRange = z.infer<typeof ReportTimeRangeSchema>

export const useBaseReportDefaultValues = (selectedLocations: AssetLocation[]): BaseReportForm => {
	const { data: preferences } = usePreferences()

	// Default radius values (in km) for different units
	const radiusKm = {
		KILOMETRES: 5,
		MILES: 8.05,
		NAUTICAL_MILES: 9.26,
	}[preferences?.unitsDistance ?? 'KILOMETRES']

	return {
		assetVrns: selectedLocations.map((location) => location.assetVrn),
		name: '',
		radiusKm,
		geometry: multiPolygon([]).geometry as BaseReportForm['geometry'],
		geometryTooComplex: false,
		geometryPending: false,
		preferences: { timezone: preferences?.timezone ?? 'UTC' },
	}
}
